/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { HomeOutlined, RightOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { paragraphXSmall, weightMedium } from 'src/components/css'
import Text from 'src/components/ts-components/Text'

export interface BreadcrumbItem {
  key: string
  /** Shown uppercased by the trail; pass it Title Cased (`capitalizeWords`). */
  label: string
  /** Clickable when set. The last item is the current place and never is. */
  onSelect?: () => void
}

interface BreadcrumbProps {
  items: BreadcrumbItem[]
  /**
   * `panel` — the Operations panel's compact trail (muted crumbs, primary on
   * hover). `header` — the page headers' trail (Team, Competition pages): a
   * home icon, links in primary, the current crumb and plain levels muted.
   */
  variant?: 'panel' | 'header'
  /** `header` only: the home icon's action (e.g. close, then go home). */
  onHome?: () => void
}

/**
 * A "Parent › Child › Current" trail. The last item is where the user is; the
 * ones before it are buttons when they have an `onSelect`.
 */
const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  variant = 'panel',
  onHome,
}) => {
  if (items.length === 0) {
    return null
  }

  const ancestors = items.slice(0, -1)
  const current = items[items.length - 1]

  if (variant === 'header') {
    // Mirrors PageHeader's trail so a full-screen screen reads like the page
    // headers: home › link › link › current.
    return (
      <nav css={headerTrailStyle} aria-label='Breadcrumb'>
        {onHome ? (
          <button
            type='button'
            onClick={onHome}
            css={headerButtonStyle}
            aria-label='Home'
          >
            <HomeOutlined css={homeIconStyle} />
          </button>
        ) : null}
        {items.map((item, index) => {
          const isCurrent = index === items.length - 1
          // Primary marks a link; the current place and plain levels are muted.
          const isLink = !isCurrent && Boolean(item.onSelect)
          const label = (
            <Text
              variant='captionSmall'
              weight='medium'
              color={isLink ? colors.PRIMARY : colors.NEUTRAL_100}
            >
              {item.label}
            </Text>
          )
          return (
            <span key={item.key} css={headerCrumbStyle}>
              {onHome || index > 0 ? (
                <RightOutlined aria-hidden css={headerSeparatorStyle} />
              ) : null}
              {isLink ? (
                <button
                  type='button'
                  onClick={item.onSelect}
                  css={headerButtonStyle}
                >
                  {label}
                </button>
              ) : (
                <span aria-current={isCurrent ? 'page' : undefined}>
                  {label}
                </span>
              )}
            </span>
          )
        })}
      </nav>
    )
  }

  return (
    <nav css={trailStyle} aria-label='Breadcrumb'>
      {ancestors.map((item) => (
        <span key={item.key}>
          {item.onSelect ? (
            <button type='button' onClick={item.onSelect} css={linkStyle}>
              {item.label}
            </button>
          ) : (
            <span>{item.label}</span>
          )}
          <span css={separatorStyle} aria-hidden>
            ›
          </span>
        </span>
      ))}
      <span css={currentStyle} aria-current='page'>
        {current.label}
      </span>
    </nav>
  )
}

export default Breadcrumb

// ---- styled: panel ----
/* No bottom margin: the parent row owns the spacing below. */
const trailStyle = css`
  ${paragraphXSmall}
  ${weightMedium}
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  color: ${colors.NEUTRAL_100};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const linkStyle = css`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  color: inherit;
  /* Buttons don't inherit text-transform from the crumb row (the UA resets it
     on form controls), so without this the clickable ancestor crumbs render in
     their source casing while only the trailing span-crumb is uppercased. */
  text-transform: inherit;
  cursor: pointer;

  &:hover {
    color: ${colors.PRIMARY};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 2px;
  }
`

/* SECONDARY_200 was all but invisible against the panel — the chevron needs to
   read as a separator at a glance, so it is brighter and a step larger than
   the crumb text it sits between. */
const separatorStyle = css`
  color: ${colors.NEUTRAL_100};
  margin-left: 6px;
  font-size: 15px;
  line-height: 1;
  vertical-align: middle;
  /* Not a letter — the crumbs' tracking would push it off its neighbours. */
  letter-spacing: 0;
`

const currentStyle = css`
  color: ${colors.NEUTRAL_50};
`

// ---- styled: header (PageHeader's look) ----
const headerTrailStyle = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`

const headerCrumbStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const headerButtonStyle = css`
  display: flex;
  align-items: center;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 2px;
  }
`

/* Icon sizes as PageHeader sets them (icons, not text). */
const homeIconStyle = css`
  font-size: 16px;
  color: ${colors.PRIMARY};
`

const headerSeparatorStyle = css`
  font-size: 10px;
  color: ${colors.NEUTRAL_100};
`
